import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const LISTING_KINDS = ["add_business", "verify_business", "fix_info", "add_photo", "report_fake"];
const ACTIVITY_KINDS = ["help_elderly", "digital_help", "blood_donation"];
const HINT = {
  add_business: "Pick a business listing you created. It is checked for duplicates before review.",
  verify_business: "A listing someone else owns. Say what you checked (visited, called, matched the address).",
  fix_info: "Tell us the wrong value and the correct one. A reviewer confirms the change.",
  add_photo: "Link a photo you took or may share. Duplicate photos are not rewarded.",
  report_fake: "Points are given only after an admin confirms the listing is fake.",
  help_elderly: "Describe the help without names or private details. Optionally add a number someone can call to confirm (kept private).",
  digital_help: "Describe the session without names or private details. Optionally add a confirmer number (kept private).",
  blood_donation: "Date and place of donation. Optionally add a confirmer number (kept private).",
};

const Field = ({ label, children }) => (
  <label className="block text-xs font-bold text-stone-700 mt-2">{label}{children}</label>
);
const inputCls = "mt-1 w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none font-normal";

export const ClaimDialog = ({ kind, label, points, onClose, onDone }) => {
  const [f, setF] = useState({});
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [idem, setIdem] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  useEffect(() => {
    setF({});
    setIdem(`${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    if (kind === "add_business") api.get("/listings/mine/all").then((r) => setMine(r.data || [])).catch(() => setMine([]));
  }, [kind]);

  const submit = async () => {
    setBusy(true);
    const { listing_id, ...evidence } = f;
    try {
      const r = await api.post("/civic/contributions", { kind, listing_id, evidence },
        { headers: { "Idempotency-Key": idem } });
      toast.success(r.data.message || "Contribution submitted — pending verification.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="vol-claim-dialog">
        <DialogHeader>
          <DialogTitle>{label} · +{points} after verification</DialogTitle>
          <DialogDescription>{HINT[kind]}</DialogDescription>
        </DialogHeader>
        {kind === "add_business" && (
          <Field label="Your listing">
            <select className={inputCls} value={f.listing_id || ""} onChange={set("listing_id")} data-testid="claim-listing-select">
              <option value="">Select…</option>
              {mine.map((l) => <option key={l.listing_id} value={l.listing_id}>{l.title}</option>)}
            </select>
          </Field>
        )}
        {LISTING_KINDS.includes(kind) && kind !== "add_business" && (
          <Field label="Listing link or ID">
            <input className={inputCls} value={f.listing_id || ""} onChange={set("listing_id")} placeholder="https://…/listing/list_…" data-testid="claim-listing-id" />
          </Field>
        )}
        {kind === "fix_info" && (
          <>
            <Field label="Field">
              <select className={inputCls} value={f.field || ""} onChange={set("field")} data-testid="claim-field">
                <option value="">Select…</option>
                {["name", "phone", "address", "hours", "category", "website", "location", "other"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Wrong value"><input className={inputCls} value={f.old_value || ""} onChange={set("old_value")} data-testid="claim-old-value" /></Field>
            <Field label="Correct value"><input className={inputCls} value={f.new_value || ""} onChange={set("new_value")} data-testid="claim-new-value" /></Field>
          </>
        )}
        {(kind === "add_photo" || kind === "verify_business") && (
          <Field label={kind === "add_photo" ? "Photo URL (https)" : "Photo URL (optional)"}>
            <input className={inputCls} value={f.photo_url || ""} onChange={set("photo_url")} data-testid="claim-photo-url" />
          </Field>
        )}
        {kind === "add_photo" && (
          <label className="flex items-center gap-2 text-xs mt-2">
            <input type="checkbox" checked={!!f.has_permission} onChange={set("has_permission")} data-testid="claim-permission" />
            I took this photo or have permission to share it
          </label>
        )}
        {ACTIVITY_KINDS.includes(kind) && (
          <>
            <Field label="Date"><input type="date" className={inputCls} value={f.activity_date || ""} onChange={set("activity_date")} data-testid="claim-activity-date" /></Field>
            <Field label="Confirmer mobile (optional · private)"><input className={inputCls} value={f.confirmer_mobile || ""} onChange={set("confirmer_mobile")} data-testid="claim-confirmer" /></Field>
          </>
        )}
        {kind !== "add_business" && kind !== "add_photo" && (
          <Field label={kind === "fix_info" ? "Note (optional)" : "What happened?"}>
            <textarea rows={3} className={inputCls} value={f.note || ""} onChange={set("note")} data-testid="claim-note" />
          </Field>
        )}
        <button onClick={submit} disabled={busy}
          className="mt-3 w-full bg-[#2D5A27] hover:bg-emerald-800 disabled:opacity-60 text-white font-black py-2 rounded-full text-sm"
          data-testid="claim-submit">
          {busy ? "Submitting…" : "Submit for verification"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
